import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { get, withPath } from '@app/api/typed';
import type { ApiData } from '@app/api/typed';
import { readingInterval } from '@app/reading';
import type { Contract, ContractView, Identity } from './ContractsModel';

export type IdentitiesPage = ApiData<'/api/v1/identities'>;
export type ContractsPage = ApiData<'/api/v1/identities/{id}/contracts'>;

export const contractsKeys = {
  /** Prefixo de tudo que é contrato — o card Estado (`security_ready`) inclusive. */
  todas: ['contracts'] as const,
  identidades: (withContract: boolean) => ['identities', { comContrato: withContract }] as const,
  vigente: (id: string) => ['contracts', 'vigente', id] as const,
  historico: (id: string) => ['contracts', 'historico', id] as const,
  versao: (id: string) => ['contracts', 'versao', id] as const,
};

/**
 * Identidades do combobox. `comContrato` liga o `com_contrato=true` do contrato:
 * T13 lista todas (contrato ausente é um estado exibível); T14 só as que já têm
 * contrato ativo (FR-071).
 */
export function useIdentities(withContract = false): UseQueryResult<Identity[], ApiError> {
  return useQuery<Identity[], ApiError>({
    queryKey: contractsKeys.identidades(withContract),
    queryFn: async () => {
      const page = await get('/api/v1/identities', {
        params: { sort: 'slug', ...(withContract ? { com_contrato: true } : {}) },
      });
      return (page.items ?? []) as Identity[];
    },
  });
}

/** Contrato vigente + políticas que concedem a mais + card Estado (FR-070). */
export function useIdentityContract(id: string | null): UseQueryResult<ContractView, ApiError> {
  return useQuery<ContractView, ApiError>({
    queryKey: contractsKeys.vigente(id ?? ''),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/contract', {
          id: id ?? '',
        }) as '/api/v1/identities/{id}/contract',
      ),
    enabled: Boolean(id),
    // «Publicado no PDP» sai da foto do L8 (aim-6ph.13): relê enquanto a leitura do PDP corre.
    refetchInterval: (query) => readingInterval(query.state.data?.state.pdp_reading),
  });
}

/** Histórico de versões do card Releases. */
export function useIdentityContracts(id: string | null): UseQueryResult<ContractsPage, ApiError> {
  return useQuery<ContractsPage, ApiError>({
    queryKey: contractsKeys.historico(id ?? ''),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/contracts', {
          id: id ?? '',
        }) as '/api/v1/identities/{id}/contracts',
      ),
    enabled: Boolean(id),
  });
}

/** Uma versão antiga do contrato (clique no card Releases). */
export function useContract(id: string | null): UseQueryResult<Contract, ApiError> {
  return useQuery<Contract, ApiError>({
    queryKey: contractsKeys.versao(id ?? ''),
    queryFn: () =>
      get(withPath('/api/v1/contracts/{id}', { id: id ?? '' }) as '/api/v1/contracts/{id}'),
    enabled: Boolean(id),
  });
}

export interface PolicyThatCites {
  id: string;
  nome: string;
}

/**
 * Políticas **ativas** que citam a identidade (`GET /policies?identity_id&estado=ativa`),
 * para o card Estado completar «nenhuma» quando há política ativa que não concede
 * além do contrato (ajustes-3, 28/08, item 11.4). A chave começa em `policies`
 * de propósito: as mutações de T12 invalidam o prefixo.
 */
export function useActivePoliciesCiting(
  identityId: string | null,
): UseQueryResult<PolicyThatCites[], ApiError> {
  return useQuery<PolicyThatCites[], ApiError>({
    queryKey: ['policies', 'citando', identityId ?? ''],
    queryFn: async () => {
      const page = await get('/api/v1/policies', {
        params: { identity_id: identityId ?? '', state: 'ativa', size: 100 },
      });
      return ((page.items ?? []) as { id: string; name: string }[]).map((p) => ({ id: p.id, nome: p.name }));
    },
    enabled: Boolean(identityId),
  });
}
